import { useTranslation } from 'react-i18next';
import { Link } from '@tanstack/react-router';
import { Network } from 'lucide-react';
import { AccountListItem } from '@bliks-io/contracts';
import {
  Button,
  Card,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@bliks-io/ui';

interface AccountGroupCardProps {
  account: AccountListItem;
  /** Every other account — a group is an account with children (LIC-09). */
  candidates: AccountListItem[];
  childAccounts: AccountListItem[];
  onSetParent: (parentAccountId: string | null) => void;
  isSaving?: boolean;
}

/**
 * Where an account sits in a group: its parent, and the accounts under it.
 *
 * The card deliberately says what the relation does NOT do. A hierarchy is
 * exactly the kind of structure that quietly acquires meaning, and an admin
 * setting a parent should not have to guess whether they just granted a
 * corporate group access to a member's data (LIC-09 — they did not).
 */
export function AccountGroupCard({
  account,
  candidates,
  childAccounts,
  onSetParent,
  isSaving
}: AccountGroupCardProps) {
  const { t } = useTranslation();
  // An account cannot be its own parent; the backend also refuses deeper
  // cycles, which this list cannot see.
  const selectable = candidates.filter((candidate) => candidate._id !== account._id);

  return (
    <Card>
      <div className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <Network className="w-5 h-5" />
          <h2 className="text-lg font-semibold">{t('accountAdmin.group')}</h2>
        </div>
        <p className="text-sm text-muted-foreground mb-4">{t('accountAdmin.groupHint')}</p>

        <div className="flex items-end gap-2 mb-4">
          <div className="flex-1">
            <label className="text-sm font-medium mb-1 block" htmlFor="parentAccount">
              {t('accountAdmin.parent')}
            </label>
            <Select
              value={account.parent?._id ?? ''}
              onValueChange={(value) => onSetParent(value || null)}
              disabled={isSaving}
            >
              <SelectTrigger id="parentAccount">
                <SelectValue placeholder={t('accountAdmin.noParent')} />
              </SelectTrigger>
              <SelectContent>
                {selectable.map((candidate) => (
                  <SelectItem key={candidate._id} value={candidate._id}>
                    {candidate.name ?? t('accountAdmin.unnamed')}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {account.parent && (
            <Button variant="outline" onClick={() => onSetParent(null)} disabled={isSaving}>
              {t('accountAdmin.detachParent')}
            </Button>
          )}
        </div>

        <div>
          <span className="text-sm font-medium">{t('accountAdmin.children')}</span>
          {childAccounts.length === 0 ? (
            <p className="text-sm text-muted-foreground mt-1">{t('accountAdmin.noChildren')}</p>
          ) : (
            <ul className="mt-1 flex flex-col gap-1">
              {childAccounts.map((child) => (
                <li key={child._id}>
                  <Link
                    to="/admin/accounts/$accountId"
                    params={{ accountId: child._id }}
                    className="text-sm text-primary hover:underline"
                  >
                    {child.name ?? t('accountAdmin.unnamed')}
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </Card>
  );
}
